/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnFiltersState, SortingState } from '@tanstack/react-table'
import { Template } from 'types/entities/Template'

type TableSearchScalar = string | number | boolean | null
export type TemplatesTableSearch =
    | TableSearchScalar
    | TableSearchScalar[]
    | Record<string, TableSearchScalar | TableSearchScalar[]>

interface TemplatesTableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
}

interface TemplatesTableMetaState {
    currentPage: number
    perPage: number
    total: number
    sortBy: string | null
    sort: 'asc' | 'desc' | null
    search: TemplatesTableSearch
}

interface TemplatesTableState {
    ui: TemplatesTableUiState
    meta: TemplatesTableMetaState | null
    hasLoaded: boolean
}

export interface TemplatesState {
    all: Template[]
    selectedTemplate: Template | null
    table: TemplatesTableState
}

const initialState: TemplatesState = {
    all: [],
    selectedTemplate: null,
    table: {
        ui: {
            pageIndex: 0,
            pageSize: 50,
            globalFilter: '',
            columnFilters: [],
            sorting: [{ id: 'id', desc: true }],
        },
        meta: null,
        hasLoaded: false,
    },
}

const templatesSlice = createSlice({
    name: 'templates',
    initialState,
    reducers: {
        setTemplates(state, action: PayloadAction<Template[]>) {
            state.all = action.payload ?? []
        },
        pushTemplate(state, action: PayloadAction<Template>) {
            const template = action.payload
            const index = state.all.findIndex((t) => t.id === template.id)

            if (index === -1) {
                state.all.push(template)
            } else {
                state.all[index] = template
            }
        },
        deleteTemplate(state, action: PayloadAction<number>) {
            const templateId = action.payload
            state.all = state.all.filter((t) => t.id !== templateId)
        },
        setTemplatesTableMeta(
            state,
            action: PayloadAction<TemplatesTableMetaState>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            state.table.meta = action.payload
            state.table.hasLoaded = true
        },
        setSelectedTemplate(state, action: PayloadAction<Template | null>) {
            state.selectedTemplate = action.payload
        },
        resetState: () => initialState,
    },
})

export const {
    setTemplates,
    pushTemplate,
    deleteTemplate,
    setTemplatesTableMeta,
    setSelectedTemplate,
    resetState,
} = templatesSlice.actions

export default templatesSlice.reducer
